/**
 * Sentinel Export Dialog (Client Feedback)
 * Collects an email + optional date range, then asks the backend to email a CSV
 * export of the reviews to that address.
 */

import { useState } from "react";
import { useAtomValue } from "jotai";
import { selectedBusinessUnitIdAtom } from "@/stores/business-unit.store";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sentinelReviewService } from "@/services/sentinelReview.service";

interface SentinelExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
}

export function SentinelExportDialog({ open, onOpenChange, defaultEmail }: SentinelExportDialogProps) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSending, setIsSending] = useState(false);

  const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const rangeInvalid = Boolean(startDate && endDate && startDate > endDate);

  const handleExport = async () => {
    if (!emailValid || rangeInvalid) return;
    setIsSending(true);
    try {
      // The export has to hold the same reviews the page above it is showing. An
      // account-wide CSV sent from a brand-scoped page is the kind of mismatch nobody
      // notices until the rows are already in someone's inbox.
      const result = await sentinelReviewService.exportReviews(
        {
          email: email.trim(),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
        selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {},
      );
      toast.success(`Export emailed to ${email.trim()} (${result.count} review${result.count === 1 ? "" : "s"})`);
      onOpenChange(false);
    } catch {
      toast.error("Could not export. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export reviews</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-muted-foreground">Send to email</label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <Input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <Input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Leave the dates empty to export all reviews. The CSV is emailed as an attachment.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isSending || !emailValid || rangeInvalid}>
            {isSending ? "Sending…" : "Export to email"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
